/* CmdbViewRenderer.js */

"use strict";

const _ = require("lodash");
const uiCore = require("@gluenetworks/steve_ui_core");
const {ComboBox} = uiCore.Control.OptionSelection.Single;

const ServiceNowComponent = require("./ServiceNow/ServiceNowComponent");
const NetBoxComponent = require("./NetBox/NetBoxComponent");
const xDomeComponent = require("./xDome/xDomeComponent");

const FancyLoadingMaskGL = require("@gluenetworks/fancy_loading_mask_gl");

/**
 * class CmdbViewRenderer
 *
 * @listens CmdbViewRenderer#event:OrgChanged
 *
 */
class CmdbViewRenderer {
    /**
     * @param {CmdbViewComponent} model - Data model from component.
     *
     */
    constructor(model) {
        this._model = model;
        this._uiUtils = this._model._uiUtils;
        this._rootElem = document.createElement("cmdb-integration-gl");
        this._rootElem.component = this._model;
        this._headerElem = document.createElement("h1");
        this._headerElem.innerText = "CMDB Integration";
        this._containerElem = document.createElement("div");
        this._containerElem.classList.add("cmdb-container");

        this._serviceComboBox = new ComboBox({
            darkTheme: true,
            required: true,
            showFilter: "false",
            options: [
                {label: "ServiceNow", value: "serviceNow"},
                {label: "NetBox", value: "netBox"},
                {label: "Claroty xDome", value: "xDome"},
            ],
        });

        /* User Data - Start */
        this._userDataStorage = this._model._storageFactory.getRemoteStorageInstance(
            "views",
            "servicenow-config"
        );
        /* User Data - End */
    }

    /*******************************************/
    /* START COMMON RENDER METHODS             */
    /*******************************************/

    async render() {
        if (!this._model) {
            return;
        }

        try {
            await this._model.whenModelIsReady();

            this._rootElem.innerHTML = "";
            this._rootElem.appendChild(this._headerElem);
            this._rootElem.appendChild(this._serviceComboBox._renderer._rootElem);
            this._rootElem.appendChild(this._containerElem);

            const cmdbServiceType = this._getDefaultCmdbServiceType();
            this._serviceComboBox.setValue(cmdbServiceType);
            this._registerListeners();

            await this.renderCmdb(cmdbServiceType);
        } catch (err) {
            console.error("Error rendering CMDB view:", err);
            throw err;
        }
    }

    async renderCmdb(cmdbServiceType = "serviceNow") {
        if (this._netBoxComponent) {
            await this._netBoxComponent.shutdown();
            this._netBoxComponent = undefined;
        }
        if (this._serviceNowComponent) {
            await this._serviceNowComponent.shutdown();
            this._serviceNowComponent = undefined;
        }
        if (this._xDomeComponent) {
            await this._xDomeComponent.shutdown();
            this._xDomeComponent = undefined;
        }
        if (cmdbServiceType === "netBox") {
            this._netBoxComponent = await this._renderService("netBox");
        } else if (cmdbServiceType === "serviceNow") {
            this._serviceNowComponent = await this._renderService("serviceNow");
        } else if (cmdbServiceType === "xDome") {
            this._xDomeComponent = await this._renderService("xDome");
        } else {
            console.warn(
                `Unknown CMDB service type: ${cmdbServiceType}. Rendering ServiceNow by default.`
            );
            this._serviceComboBox.setValue("serviceNow");
            this._serviceNowComponent = await this._renderService("serviceNow");
        }
    }

    /**
     * Renders one provider behind a loading mask. The mask is removed in a finally block so that a
     * failed render surfaces as an error instead of leaving the screen stuck in process.
     */
    async _renderService(cmdbServiceType) {
        const component = this._initializeService(cmdbServiceType);
        this._containerElem.innerHTML = "";

        const loader = new FancyLoadingMaskGL({
            shown: true,
            backgroundHidden: true,
            darkTheme: true,
        });
        const loaderElem = loader.renderer.getRootElement();
        this._containerElem.appendChild(loaderElem);

        this._containerElem.appendChild(component.renderer.rootElement);

        try {
            await component.renderer.render();
        } finally {
            loaderElem.remove();
        }

        return component;
    }

    /********************************************/
    /* END COMMON RENDER METHODS                */
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/

    _registerListeners() {
        this._model.on("OrgChanged", (this._onOrgChanged = this._onOrgChanged.bind(this)));
        this._serviceComboBox.on(
            "UserValue",
            (this._onServiceChange = this._onServiceChange.bind(this))
        );
    }

    _unregisterListeners() {
        this._model.removeListener("OrgChanged", this._onOrgChanged);
        this._serviceComboBox.removeListener("UserValue", this._onServiceChange);
    }

    _getDefaultCmdbServiceType() {
        if (this._model._snDataSyncConfigEntity && this._model._snDataSyncConfigEntity.enabled) {
            return "serviceNow";
        }
        if (this._model._nbDataSyncConfigEntity && this._model._nbDataSyncConfigEntity.enabled) {
            return "netBox";
        }
        if (this._model._xdDataSyncConfigEntity && this._model._xdDataSyncConfigEntity.enabled) {
            return "xDome";
        }
        return "serviceNow";
    }

    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/

    shutdown() {
        this._unregisterListeners();

        let shutdowns = [];

        if (this._serviceNowComponent) {
            shutdowns.push(this._serviceNowComponent.shutdown());
        }

        if (this._netBoxComponent) {
            shutdowns.push(this._netBoxComponent.shutdown());
        }

        if (this._xDomeComponent) {
            shutdowns.push(this._xDomeComponent.shutdown());
        }

        // Remove root element from DOM
        if (this._rootElem && this._rootElem.remove) {
            this._rootElem.remove();
        }

        // Wait for all shutdowns to complete
        return Promise.all(shutdowns).then(
            () => {
                // Clear properties
                this._model = undefined;
                this._rootElem = undefined;
                this._headerElem = undefined;
                this._containerElem = undefined;
                this._serviceComboBox = undefined;
                this._userDataStorage = undefined;
                this._serviceNowComponent = undefined;
                this._netBoxComponent = undefined;
                this._xDomeComponent = undefined;
            },
            (err) => {
                console.log("Error shutting down CmdbViewRenderer: ", err);
            }
        );
    }

    get rootElement() {
        return this.getRootElement();
    }

    getRootElement() {
        return this._rootElem;
    }

    get model() {
        return this.getModel();
    }

    getModel() {
        return this._model;
    }

    /*******************************************/
    /* END COMPONENT RENDERER METHODS          */
    /* START PRIVATE RENDER METHODS            */
    /*******************************************/

    _initializeService(cmdbServiceType) {
        if (cmdbServiceType === "serviceNow") {
            return new ServiceNowComponent(
                this._model._userSession,
                this._model._uiUtils,
                this._model._logger,
                this._model._muf,
                this._model._organizationService,
                this._model._DataSyncConfigEntityDef,
                this._model._steveIo,
                this._model._DeviceEntityDef,
                this._model._ExplorablesEntityDef,
                this._model._storageFactory,
                this._model._ValueMapEntityDef,
                this._model._router,
                this._model._snDataSyncConfigEntity,
                this._model._snServiceConnectionEntities,
                this._model._ServiceConnectionEntityDef
            );
        } else if (cmdbServiceType === "netBox") {
            return new NetBoxComponent(
                this._model._userSession,
                this._model._uiUtils,
                this._model._logger,
                this._model._muf,
                this._model._organizationService,
                this._model._DataSyncConfigEntityDef,
                this._model._steveIo,
                this._model._DeviceEntityDef,
                this._model._ExplorablesEntityDef,
                this._model._storageFactory,
                this._model._ValueMapEntityDef,
                this._model._router,
                this._model._nbDataSyncConfigEntity,
                this._model._nbServiceConnectionEntities,
                this._model._CustomFieldsEntityDef,
                this._model._SiteEntityDef,
                this._model._ServiceConnectionEntityDef
            );
        } else if (cmdbServiceType === "xDome") {
            return new xDomeComponent(
                this._model._userSession,
                this._model._uiUtils,
                this._model._logger,
                this._model._muf,
                this._model._organizationService,
                this._model._DataSyncConfigEntityDef,
                this._model._steveIo,
                this._model._DeviceEntityDef,
                this._model._ExplorablesEntityDef,
                this._model._storageFactory,
                this._model._ValueMapEntityDef,
                this._model._router,
                this._model._xdDataSyncConfigEntity,
                this._model._xdServiceConnectionEntities,
                this._model._CustomFieldsEntityDef,
                this._model._ServiceConnectionEntityDef
            );
        }
    }

    /*******************************************/
    /* END PRIVATE RENDER METHODS              */
    /* START HANDLER METHODS                   */
    /*******************************************/

    async _onOrgChanged() {
        const currentCmdbServiceType = this._serviceComboBox.getValue();
        const isNetBoxEnabled =
            this._model._nbDataSyncConfigEntity && this._model._nbDataSyncConfigEntity.enabled;
        const isServiceNowDisabled =
            this._model._snDataSyncConfigEntity && !this._model._snDataSyncConfigEntity.enabled;

        const isxDomeEnabled =
            this._model._xdDataSyncConfigEntity && this._model._xdDataSyncConfigEntity.enabled;

        let cmdbServiceType = "serviceNow";

        switch (currentCmdbServiceType) {
            case "netBox":
                if (isNetBoxEnabled) {
                    cmdbServiceType = "netBox";
                } else if (isxDomeEnabled) {
                    cmdbServiceType = "xDome";
                } else {
                    cmdbServiceType = "serviceNow";
                }
                break;
            case "serviceNow":
                if (!isServiceNowDisabled) {
                    cmdbServiceType = "serviceNow";
                } else if (isNetBoxEnabled) {
                    cmdbServiceType = "netBox";
                } else if (isxDomeEnabled) {
                    cmdbServiceType = "xDome";
                } else {
                    cmdbServiceType = "netBox";
                }
                break;
            case "xDome":
                if (isxDomeEnabled) {
                    cmdbServiceType = "xDome";
                } else if (isNetBoxEnabled) {
                    cmdbServiceType = "netBox";
                } else {
                    cmdbServiceType = "serviceNow";
                }
                break;
        }
        this._serviceComboBox.setValue(cmdbServiceType);

        try {
            await this.renderCmdb(cmdbServiceType);
        } catch (err) {
            this._showError(err);
        }
    }

    async _onServiceChange(newValue, oldValue) {
        let cmdbServiceType = newValue;
        if (oldValue !== cmdbServiceType) {
            const dirtyEnabledService = [
                ["netBox", this._model._nbDataSyncConfigEntity],
                ["serviceNow", this._model._snDataSyncConfigEntity],
                ["xDome", this._model._xdDataSyncConfigEntity],
            ].find(([service, entity]) => {
                return service === oldValue && entity.isDirty() && entity.getEnabled();
            });
            if (dirtyEnabledService) {
                await this._model._muf.hideDisplayed();
                try {
                    await this._model._muf.displayConfirmation(
                        "Would you like to Save or Discard your changes?",
                        {
                            title: "Unsaved Changes",
                            confirmDetails: {
                                text: "Save",
                                type: "text",
                            },
                            rejectDetails: {
                                text: "Discard",
                                type: "destructive",
                            },
                        }
                    );
                    //save entity
                    const prevServiceType = dirtyEnabledService[0];
                    if (prevServiceType === "netBox") {
                        this._netBoxComponent && (await this._netBoxComponent._saveEntity());
                    } else if (prevServiceType === "serviceNow") {
                        this._serviceNowComponent &&
                            (await this._serviceNowComponent._saveEntity());
                    } else if (prevServiceType === "xDome") {
                        this._xDomeComponent && (await this._xDomeComponent._saveEntity());
                    }
                } catch (err) {
                    if (cmdbServiceType === "netBox") {
                        this._serviceNowComponent && this._serviceNowComponent.resetAllPatches();
                        this._model._xdDataSyncConfigEntity.clearPendingPatches();
                    } else if (cmdbServiceType === "serviceNow") {
                        this._model._nbDataSyncConfigEntity.clearPendingPatches();
                        this._model._xdDataSyncConfigEntity.clearPendingPatches();
                    } else if (cmdbServiceType === "xDome") {
                        this._model._nbDataSyncConfigEntity.clearPendingPatches();
                        this._serviceNowComponent && this._serviceNowComponent.resetAllPatches();
                    }
                }
            }
        }
        try {
            await this.renderCmdb(cmdbServiceType);
        } catch (err) {
            this._showError(err);
        }
    }

    _showError(error) {
        if (_.get(error, "errorCode") !== "NoPendingUpdates") {
            this._uiUtils.showMessageDialog({
                message: "An error occurred: " + this._uiUtils.errorCodeMapping(error),
                confirmButtonName: "OK",
            });
        }
    }

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE API METHODS               */
    /*******************************************/

    /*******************************************/
    /* END PRIVATE API METHODS               */
    /*******************************************/
}

module.exports = CmdbViewRenderer;
